import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Trash, ArrowRight, ShoppingBag, Check } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import PayIn4, { usePaymentOptions } from "@/components/PayIn4";

export default function Cart() {
  const { items, remove, total, sessionMap } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const payOpts = usePaymentOptions();
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [paymentType, setPaymentType] = useState("full"); // "full" | "deposit"

  const chargeNow = paymentType === "deposit" ? total * 0.5 : total;
  const balanceDue = paymentType === "deposit" ? total - chargeNow : 0;
  const showKlarna = !!payOpts.klarna && paymentType === "full";

  const checkout = async (method = "card") => {
    setError("");
    if (!user) { navigate("/login?next=/cart"); return; }
    if (items.length === 0) return;
    setLoading(method);
    try {
      // Build session_ids map: { course_id: class_session_id }
      const session_ids = {};
      items.forEach((i) => { if (sessionMap[i.id]) session_ids[i.id] = sessionMap[i.id].id; });
      const { data } = await api.post("/checkout/session", {
        course_ids: items.map(i => i.id),
        origin_url: window.location.origin,
        session_ids: Object.keys(session_ids).length ? session_ids : undefined,
        payment_type: paymentType,
        method,
      });
      // Meta Pixel: fire InitiateCheckout right before redirecting to Stripe
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "InitiateCheckout", {
          value: chargeNow,
          currency: "USD",
          num_items: items.length,
          content_ids: items.map(i => i.id),
          content_type: "product",
        });
      }
      window.location.href = data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Checkout failed. Please try again.");
      setLoading("");
    }
  };

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  return (
    <div data-testid="cart-page">
      <Seo title="Cart" path="/cart" noindex />
      <section className="border-b border-white/10 pt-24 pb-12">
        <div className="container-x">
          <div className="overline mb-4">// CART</div>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tighter">Your enrollment</h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid grid-cols-12 gap-12">
          <div className="col-span-12 lg:col-span-8">
            {items.length === 0 ? (
              <div className="surface-card p-16 text-center" data-testid="cart-empty">
                <ShoppingBag size={48} weight="duotone" className="text-[#FFD300] mx-auto mb-4" />
                <h2 className="font-heading text-2xl mb-3">Your cart is empty</h2>
                <p className="text-neutral-400 mb-6">Browse the academy to find your next certification.</p>
                <Link to="/training" className="btn-primary inline-flex">Browse Courses <ArrowRight size={14} /></Link>
              </div>
            ) : (
              <div className="space-y-px bg-white/10">
                {items.map((c) => (
                  <div key={c.id} className="bg-[#0A0A0A] p-6 flex flex-col sm:flex-row gap-6 items-start" data-testid={`cart-item-${c.id}`}>
                    <img src={c.image} alt={c.title} className="w-full sm:w-32 h-24 object-cover" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="code-badge">{c.code}</span>
                        {c.state && <span className="state-tag">{c.state}</span>}
                      </div>
                      <h3 className="font-heading text-xl tracking-tight">{c.title}</h3>
                      <div className="font-mono text-xs text-neutral-500 mt-2">{c.duration_hours} hours · {c.level}</div>
                      {sessionMap[c.id] ? (
                        <div className="font-mono text-xs text-[#FFD300] mt-3 flex items-center gap-2">
                          <span className="inline-block w-1.5 h-1.5 bg-[#FFD300] rounded-full" />
                          Class date: {fmtDate(sessionMap[c.id].starts_at)} · {sessionMap[c.id].venue}
                        </div>
                      ) : c.manual_schedule ? (
                        <div className="font-mono text-xs text-neutral-400 mt-3" data-testid="cart-dates-tba">Class dates coming soon — we'll email you the schedule</div>
                      ) : (
                        <Link to={`/training/${c.slug}`} className="font-mono text-xs text-neutral-500 hover:text-[#FFD300] mt-3 inline-block">
                          + Pick a class date
                        </Link>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <div className="font-heading text-2xl">${c.price.toFixed(0)}</div>
                      <button onClick={() => remove(c.id)} className="btn-ghost text-neutral-400 hover:text-[#FFD300]" data-testid={`remove-${c.id}`}>
                        <Trash size={14} /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <aside className="col-span-12 lg:col-span-4">
            <div className="sticky top-24 surface-card p-8" data-testid="cart-summary">
              <div className="overline mb-4">// SUMMARY</div>
              <div className="space-y-3 mb-6 pb-6 border-b border-white/10">
                <div className="flex justify-between text-sm text-neutral-300"><span>Items</span><span className="font-mono">{items.length}</span></div>
                <div className="flex justify-between text-sm text-neutral-300"><span>Subtotal</span><span className="font-mono">${total.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm text-neutral-300"><span>Processing fee</span><span className="font-mono">$0.00</span></div>
              </div>

              {/* Payment option toggle — 50% deposit reserves the seat, balance due before class */}
              {items.length > 0 && (
                <div className="mb-6" data-testid="payment-type-selector">
                  <div className="overline mb-3">// Payment Option</div>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setPaymentType("full")}
                      className={`w-full text-left border p-4 transition-colors ${paymentType === "full" ? "border-[#FFD300] bg-[#FFD300]/5" : "border-white/10 hover:border-white/25"}`}
                      data-testid="payment-type-full"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center flex-shrink-0 ${paymentType === "full" ? "border-[#FFD300] bg-[#FFD300]" : "border-white/30"}`}>
                          {paymentType === "full" && <Check size={10} weight="bold" className="text-black" />}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-white font-medium">Pay in full</div>
                          <div className="text-xs text-neutral-400 mt-1">Instant enrollment · <span className="font-mono">${total.toFixed(2)}</span> today</div>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentType("deposit")}
                      className={`w-full text-left border p-4 transition-colors ${paymentType === "deposit" ? "border-[#FFD300] bg-[#FFD300]/5" : "border-white/10 hover:border-white/25"}`}
                      data-testid="payment-type-deposit"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center flex-shrink-0 ${paymentType === "deposit" ? "border-[#FFD300] bg-[#FFD300]" : "border-white/30"}`}>
                          {paymentType === "deposit" && <Check size={10} weight="bold" className="text-black" />}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-white font-medium">50% deposit — reserve your seat</div>
                          <div className="text-xs text-neutral-400 mt-1">
                            <span className="font-mono">${(total * 0.5).toFixed(2)}</span> today · <span className="font-mono text-[#FFD300]">${(total * 0.5).toFixed(2)}</span> balance due before class
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-baseline mb-2">
                <span className="overline">{paymentType === "deposit" ? "Due today" : "Total Due"}</span>
                <span className="font-heading text-4xl" data-testid="cart-charge-now">${chargeNow.toFixed(2)}</span>
              </div>
              {paymentType === "full" && <PayIn4 price={total} className="text-right mb-2" testId="cart-pay-in-4" />}
              {balanceDue > 0 && (
                <div className="flex justify-between items-baseline mb-6 text-xs" data-testid="cart-balance-due">
                  <span className="font-mono uppercase tracking-[0.2em] text-neutral-500">Balance before class</span>
                  <span className="font-mono text-[#FFD300]">${balanceDue.toFixed(2)}</span>
                </div>
              )}
              {balanceDue === 0 && <div className="mb-6" />}

              {error && <div className="text-sm text-red-400 mb-4 font-mono" data-testid="checkout-error">{error}</div>}

              <button onClick={() => checkout("card")} disabled={!!loading || items.length === 0} className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed" data-testid="checkout-btn">
                {loading === "card" ? "Processing..." : user ? (paymentType === "deposit" ? "Reserve My Seat" : "Proceed to Checkout") : "Login & Checkout"} <ArrowRight size={14} />
              </button>
              {showKlarna && (
                <button onClick={() => checkout("klarna")} disabled={!!loading || items.length === 0}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-[#FFB3C7] bg-[#FFB3C7] text-black font-mono text-xs tracking-[0.15em] uppercase py-3.5 hover:bg-[#ffc2d2] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="checkout-klarna-btn">
                  {loading === "klarna" ? "Opening Klarna..." : "Pay in 4 with Klarna"}
                </button>
              )}
              <div className="text-xs text-neutral-500 mt-4 leading-relaxed font-mono" data-testid="checkout-methods-note">
                {payOpts.wallets ? "Card · Apple Pay · Google Pay · Cash App Pay" : "Secure card checkout"}
                {payOpts.afterpay && paymentType === "full" ? " · Afterpay" : ""}
                {" · "}{paymentType === "deposit" ? "Balance payable anytime from your dashboard" : "Instant enrollment after payment"}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
